import React, { createContext, useContext, useMemo, useState } from 'react';
import { login as loginApi, signup as signupApi } from '../api/authApi';

export type AuthUser = {
  id: number;
  email: string;
  username: string;
};

type AuthContextValue = {
  accessToken: string | null;
  user: AuthUser | null;
  login: (args: { email: string; password: string }) => Promise<void>;
  signup: (args: { email: string; password: string; username: string }) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const ACCESS_TOKEN_KEY = 'dict_access_token';
const USER_KEY = 'dict_user';

function readAccessToken() {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  return token ? token : null;
}

function readUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Initialize synchronously so route guards/redirects behave correctly on first render.
  const [accessToken, setAccessToken] = useState<string | null>(() => readAccessToken());
  const [user, setUser] = useState<AuthUser | null>(() => readUser());

  const value = useMemo<AuthContextValue>(() => {
    return {
      accessToken,
      user,
      login: async ({ email, password }) => {
        const { access_token, user: userObj } = await loginApi({ email, password });
        localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
        localStorage.setItem(USER_KEY, JSON.stringify(userObj));
        setAccessToken(access_token);
        setUser(userObj);
      },
      signup: async ({ email, password, username }) => {
        await signupApi({ email, password, username });
        // Keep the flow simple: user will still login after signup.
      },
      logout: () => {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setAccessToken(null);
        setUser(null);
      },
    };
  }, [accessToken, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

